import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { formatDateRangeByMonth } from '@/utils/date';
import { LinearGradient } from 'expo-linear-gradient';
import { PromoImageCard } from './promo-image-card';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { VolumePromotion } from '@/utils/volume-promotions-types';
import { VolumePromotionDateLabel } from './volume-promotion-date-label';

interface Props {
  marketingTextStyle?: StyleProp<TextStyle>;
  promotion: VolumePromotion;
  style?: StyleProp<ViewStyle>;
}

export const VolumePromotionBanner: React.FC<Props> = ({
  promotion,
  style,
  marketingTextStyle,
}) => {
  const [styles] = useStyles();

  const dateLabel = useMemo(() => {
    const nowMoment = moment().tz(promotion.supplierTimezone);
    const startDateMoment = moment.tz(
      promotion.startsAt,
      promotion.supplierTimezone,
    );
    const endDateMoment = moment.tz(
      promotion.expiresAt,
      promotion.supplierTimezone,
    );

    if (
      nowMoment.isAfter(startDateMoment) &&
      nowMoment.isBetween(
        moment(endDateMoment).subtract(1, 'week'),
        endDateMoment,
      )
    ) {
      // Promotion is live with less than a week left, display "5 days left"
      const daysLeft = Math.max(endDateMoment.diff(nowMoment, 'days'), 1);
      return i18n.t(daysLeft === 1 ? 'day_left' : 'days_left', {
        days: daysLeft,
      });
    }

    return formatDateRangeByMonth(
      startDateMoment,
      endDateMoment,
      promotion.supplierTimezone,
    );
  }, [promotion]);

  const labelColor = promotion.backgroundColorStart;

  const hasImageBackground =
    promotion.backgroundImageUrl && promotion.backgroundImageUrl.trim() !== '';

  // Use ImageBackground if available and no errors
  if (hasImageBackground) {
    return (
      <View style={[styles.baseImage, style]}>
        <PromoImageCard
          dateLabel={dateLabel}
          imageUri={promotion.backgroundImageUrl!}
          title={promotion.marketingText ?? ''}
        />
      </View>
    );
  }

  // Fallback to LinearGradient for color backgrounds
  return (
    <LinearGradient
      colors={[promotion.backgroundColorStart, promotion.backgroundColorEnd]}
      end={[1, 1]}
      start={[0, 0]}
      style={[styles.base, style]}
    >
      <VolumePromotionDateLabel
        dateLabel={dateLabel}
        textColor={labelColor}
        variant="white"
      />
      <Text style={[marketingTextStyle, styles.text]}>
        {promotion.marketingText}
      </Text>
    </LinearGradient>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    padding: 16,
  },
  baseImage: {
    flexDirection: 'row',
    height: '100%',
  },
  text: {
    ...FONTS.primaryBold,
    color: theme.sk.color.bg.primary.default,
  },
}));
